import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { fetchEventDetail } from "@/api/events";
import { useLang } from "@/i18n";
import PageHeader from "@/components/PageHeader";

const AUTO_SLIDE_MS = 4000;

export default function EventDetails() {
  const { eventId } = useParams();
  const location = useLocation();
  const { t, fmtDate } = useLang();
  const [event, setEvent] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const fallbackTitle = location.state?.eventTitle || "";
  const fallbackDate = location.state?.eventDate || "";
  const pageTitle = event?.title || fallbackTitle;
  const pageDate = event?.date || fallbackDate;

  useEffect(() => {
    setActiveImage(0);
    fetchEventDetail(eventId).then(setEvent).catch(() => setEvent(null));
  }, [eventId]);

  useEffect(() => {
    if (!event?.images?.length || event.images.length < 2) return undefined;

    const timer = window.setInterval(() => {
      setActiveImage(index => (index + 1) % event.images.length);
    }, AUTO_SLIDE_MS);

    return () => window.clearInterval(timer);
  }, [event?.images]);

  const images = event?.images || [];
  const hasImages = images.length > 0;
  const showControls = images.length > 1;

  const goToPrevious = () => {
    setActiveImage(index => (index - 1 + images.length) % images.length);
  };

  const goToNext = () => {
    setActiveImage(index => (index + 1) % images.length);
  };

  return (
    <div data-testid="event-detail-page">
      <PageHeader
        eyebrow={t.events.eyebrow}
        title={pageTitle}
        sub={pageDate ? fmtDate(pageDate) : t.events.sub}
        titleClassName="!text-[1.75rem] sm:!text-[1.75rem] lg:!text-[1.75rem] !leading-[1.4]"
      />

      <section className="section-pad">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/events" className="btn-ghost inline-flex">
            <ChevronLeft size={14} /> {t.common.back}
          </Link>

          {event ? (
            <article className="mt-8 card-soft overflow-hidden">
              <div className="p-6 sm:p-8 lg:p-10">
                {pageDate && (
                  <div className="inline-flex items-center gap-2 bg-white/95 px-3 py-1.5 rounded-full text-[12px] font-semibold text-[#7B1818]">
                    <Calendar size={14} /> {fmtDate(pageDate)}
                  </div>
                )}

                {event.englishText && (
                  <p className="mt-6 text-[15.5px] text-[#5C4D42] leading-8 whitespace-pre-line">
                    {event.englishText}
                  </p>
                )}

                {hasImages && (
                  <div className="mt-8">
                    <div className="relative aspect-[16/10] overflow-hidden rounded-[28px] bg-[#EFE3CC]">
                      <img
                        src={images[activeImage]}
                        alt={`${event.title || "Event"} ${activeImage + 1}`}
                        className="w-full h-full object-cover"
                      />

                      {showControls && (
                        <>
                          <button
                            type="button"
                            onClick={goToPrevious}
                            className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#FDFBF7]/90 text-[#7B1818] shadow-md flex items-center justify-center"
                            aria-label="Previous image"
                          >
                            <ChevronLeft size={18} />
                          </button>
                          <button
                            type="button"
                            onClick={goToNext}
                            className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#FDFBF7]/90 text-[#7B1818] shadow-md flex items-center justify-center"
                            aria-label="Next image"
                          >
                            <ChevronRight size={18} />
                          </button>
                        </>
                      )}
                    </div>

                    {showControls && (
                      <div className="mt-4 flex flex-wrap justify-center gap-2">
                        {images.map((image, index) => (
                          <button
                            key={image}
                            type="button"
                            onClick={() => setActiveImage(index)}
                            className={`h-2.5 rounded-full transition-all ${
                              index === activeImage ? "w-8 bg-[#7B1818]" : "w-2.5 bg-[#D4AF37]/50"
                            }`}
                            aria-label={`View image ${index + 1}`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {event.kannadaText && (
                  <p className="mt-8 text-[15.5px] text-[#5C4D42] leading-8 whitespace-pre-line">
                    {event.kannadaText}
                  </p>
                )}
              </div>
            </article>
          ) : (
            <div className="mt-8 card-soft p-8 text-[#5C4D42]">{t.events.empty}</div>
          )}
        </div>
      </section>
    </div>
  );
}
